/* eslint-disable no-param-reassign */
/* eslint no-shadow: ["error", { "allow": ["state", "getters"] }] */
import {
  omit, pick, set, get,
} from 'lodash-es';
import {
  account,
  login as _login,
  autoLogin as _autologin,
  resetLink as _resetLink,
  resetPassword as _resetPassword,
  updatePassword as _updatePassword,
  updateUser as _updateUser,
  disconnect as _disconnect,
  findMeetingById,
  cancelMeetingById,
  createMember,
  deleteMemberById,
  clientMe,
  switchAccount,
} from '@/services/api/customer/account';

import { findAllMembersByCompanyId } from '@/services/api/customer/company';

import { validateDownloadForm } from '@/utils/validators';

// initial state
const state = {
  id: null,
  clientId: null,
  isLogged: false,
  isBroker: false,
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  role: '',
  position: '',
  password: '',
  civility: null,
  birthDate: '',
  hasSetPassword: true,
  meetings: [],
  members: [],
  additionalCompanies: [],
  newUser: {
    firstName: '',
    lastName: '',
    position: '',
    email: '',
  },
  signedDocument: {},
};
// getters
const getters = {
  get: (state) => pick(state, ['email', 'firstName', 'lastName', 'phoneNumber', 'role', 'position']),
  salerep: (state) => state.salerep,
  hasSetPassword: (state) => state.hasSetPassword,
  clientCredentials: (state) => pick(state, ['email', 'id', 'clientId']),
  getOffline: (state) => pick(state, ['email', 'firstName', 'lastName', 'phoneNumber', 'civility', 'birthDate']),
  accountId: (state) => state.id,
  userRegister: (state) => omit(state, 'isLogged'),
  isLogged: (state) => state.isLogged,
  isBroker: (state) => state.isBroker,
  haveLead: (state) => validateDownloadForm(state),
  meetings: (state) => state.meetings,
  members: (state) => state.members,
  additionalCompanies: (state) => state.additionalCompanies,
  newUser: (state) => state.newUser,
  signedDocument: (state) => state.signedDocument,
};

// actions
const actions = {
  clear({ commit }) {
    commit('CLEAR');
  },
  async getClient({ commit }, accountId) {
    const success = await account(accountId);
    commit('SET', pick(success.data, ['email', 'firstName', 'lastName', 'phoneNumber', 'role']));
  },
  async findMeetingById({ commit, state }) {
    const { data } = await findMeetingById(state.clientId);
    commit('setValue', { meetings: data });
    return data;
  },

  async authenticate({ commit, dispatch }) {
    const me = await clientMe();
    const { data } = await account(me.data.id);
    const { id, user, company } = data;
    const client = pick(user, ['email', 'firstName', 'lastName', 'phoneNumber', 'position', 'hasSetPassword']);
    const salerep = company.saleRep ? company.saleRep.id : null;
    const companyId = company.id;
    const clientId = user.id;
    const isLogged = true;
    const isBroker = data.role.id === 9;
    try {
      commit('setValue', { additionalCompanies: me.data.additionalCompanies });
      await dispatch('set', {
        id, clientId, isBroker, ...client, role: client.position, isLogged, salerep, hasSetPassword: client.hasSetPassword,
      });
      await dispatch('payments/setExternal',
        get(data, 'company.stripePaymentExternalReference') || null, { root: true });
      await dispatch('company/setCompanyById', companyId, { root: true });
      await dispatch('company/setCompanyById', companyId, { root: true });
      await dispatch('invoices/setInvoicesItems', companyId, { root: true });
      await dispatch('orders/setOrders', companyId, { root: true });
      const haveSignature = await dispatch('company/getDocumentsToBeSigned', null, { root: true });
      return Promise.resolve({ isLogged: true, haveSignature });
    } catch (err) {
      return Promise.resolve({ isLogged: false, haveSignature: false });
    }
  },
  async login({ dispatch }, payload) {
    try {
      await _login(payload.email, payload.password);
      await dispatch('authenticate');
    } catch (err) {
      dispatch('reporting/sendError', { err, currentPage: 'Login' }, { root: true });
      throw (err);
    }
  },
  async updateUser({ state, dispatch }) {
    const client = pick(state, ['email', 'firstName', 'lastName', 'phoneNumber', 'position']);
    const { clientId } = state;
    try {
      await _updateUser({ ...client, id: clientId }, clientId);
    } catch (err) {
      dispatch('reporting/sendError', { err }, { root: true });
    }
  },
  async resetLink({ dispatch }, email) {
    try {
      await _resetLink(email);
    } catch (err) {
      dispatch('reporting/sendError', { err }, { root: true });
    }
  },
  async resetPassword(unset, {
    token, id, password, confirmPassword,
  }) {
    await _resetPassword({
      token, id, password, confirmPassword,
    });
    return Promise.resolve('success');
  },
  async updatePassword({ state, dispatch }, infos) {
    const { clientId, email } = state;
    try {
      await _updatePassword({ ...infos, email }, clientId);
      dispatch('setValue', { hasSetPassword: true });
    } catch (err) {
      dispatch('reporting/sendError', { err }, { root: true });
      throw (err);
    }
  },
  async cancelMeeting({ state, dispatch }) {
    const [lastMeeting] = [...state.meetings]
      .filter(({ status }) => status === 'CONFIRMED')
      .sort(({ startAt: a }, { startAt: b }) => {
        const aDate = (new Date(a)).getTime();
        const bDate = (new Date(b)).getTime();
        return aDate - bDate;
      }) || [];
    if (lastMeeting) {
      await cancelMeetingById(lastMeeting.id);
      await dispatch('findMeetingById');
    }
    return Promise.resolve();
  },

  async getMembers({ rootGetters, commit }) {
    const companyId = rootGetters['company/companyId'];
    const { data } = await findAllMembersByCompanyId(companyId);
    commit('setValue', { members: data });

    return data;
  },

  async addMember({ state, rootGetters, commit }) {
    const comp = rootGetters['company/get'];
    const member = {
      company: comp,
      user: state.newUser,
    };
    await createMember({ ...member });
    commit('setValue', { newUser: {} });
  },

  /**
   * create new account
   */
  async addCompanyMember({ state }, comp) {
    const client = pick(state, ['email', 'firstName', 'lastName', 'phoneNumber', 'role', 'position']);
    const member = {
      company: comp,
      user: client,
    };
    await createMember({ ...member });
  },

  async removeMember({ state, commit }, id) {
    await deleteMemberById(id);
    const members = state.members
      .filter(({ id: memberId }) => id !== memberId)
      || [];
    commit('setValue', { members });
  },

  async switchAccount({ dispatch }, accountId) {
    try {
      await switchAccount(accountId);
    } catch (err) {
      dispatch('reporting/sendError', { err }, { root: true });
    }
  },

  async disconnect({ commit }) {
    await _disconnect();
    commit('setLogged', false);
  },
  async handleAutoAuthentication({ commit }, autoLoginToken) {
    try {
      await _autologin(autoLoginToken);
      commit('setLogged', true);
    } catch (err) {
      commit('setLogged', false);
      throw (err);
    }
  },
  setSignedDocument({ commit }, value) {
    commit('setSignedDocument', value);
  },
  set({ commit }, information) {
    commit('set', information);
    return Promise.resolve();
  },
  unsetPassword({ commit }) {
    commit('UNSET_PASSWORD');
  },
  setValue({ commit }, object) {
    commit('setValue', object);
  },
  setValueWithPath({ commit }, object) {
    commit('setValueWithPath', object);
  },
};

// mutations
const mutations = {
  UNSET_PASSWORD(state) {
    state.password = '';
    state.confirmPassword = '';
  },
  CLEAR(state) {
    state.isLogged = false;
    state.email = '';
    state.firstName = '';
    state.lastName = '';
    state.phoneNumber = '';
    state.role = '';
    state.position = '';
    state.password = '';
    state.confirmPassword = '';
    state.civility = null;
    state.birthDate = '';
  },
  set(state, information) {
    Object.assign(state, { ...information });
  },
  setLogged(state, value) {
    state.isLogged = value;
  },
  setSignedDocument(state, value) {
    state.signedDocument = value;
  },
  setValue(state, object) {
    const [key] = Object.keys(object);
    state[key] = object[key];
  },
  setValueWithPath(state, { value, path }) {
    set(state, path, value);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
